/**
 * apps/katalog-app/src/pages/components/AuthForm.tsx
 *
 * Reusable auth form — passwordless email + Google OAuth.
 * No password fields. Uses requestEmailSignIn for OTP.
 */
import { useState } from 'react';
import { Mail, ArrowRight } from 'lucide-react';
import { Button, Input, Label } from '@bright-web-skeleton/ui';
import { useAuth, SessionExpiredBanner, VerifyEmailScreen } from '@bright-web-skeleton/auth';
import { toast } from 'sonner';

const GoogleIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24">
    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

export interface AuthFormProps {
  onSuccess?: (userId: string) => void;
  compact?: boolean;
}

export const AuthForm: React.FC<AuthFormProps> = ({ onSuccess, compact = false }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  const { requestEmailSignIn, loginWithGoogle, clearSessionExpired } = useAuth();

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    setIsLoading(true);
    clearSessionExpired();

    try {
      const result = await requestEmailSignIn(email.trim());
      if (result.ok) {
        try { localStorage.setItem('bright_last_user_email', email); } catch { /* noop */ }
        setPendingEmail(email.trim());
      } else {
        toast.error(result.error || 'Failed to send sign-in link');
      }
    } catch (error: any) {
      toast.error(error.message || 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    clearSessionExpired();
    try {
      await loginWithGoogle('/auth/callback');
    } catch (error: any) {
      toast.error(error.message || 'Google sign in failed.');
      setIsLoading(false);
    }
  };

  // Show check-email screen after OTP sent
  if (pendingEmail) {
    return (
      <VerifyEmailScreen
        pendingEmail={pendingEmail}
        initialCooldownSeconds={60}
        onBack={() => setPendingEmail(null)}
      />
    );
  }

  return (
    <div className={compact ? '' : 'w-full max-w-md mx-auto'}>
      {!compact && (
        <div className="text-center mb-6">
          <div className="flex items-center justify-center space-x-2 mb-3">
            <img src="/brand/katalag-logo.png" alt="Katalag" className="h-8 w-8 rounded-lg object-contain" />
            <span className="font-bold text-xl">Katalag</span>
          </div>
          <h2 className="text-lg font-semibold">Sign in to continue</h2>
          <p className="text-sm text-muted-foreground">Works for new and returning users</p>
        </div>
      )}

      <SessionExpiredBanner />

      <div className={compact ? 'space-y-4' : ''}>
        {/* Google Sign In */}
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={handleGoogleSignIn}
          disabled={isLoading}
        >
          <GoogleIcon />
          <span className="ml-2">Continue with Google</span>
        </Button>

        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card px-2 text-muted-foreground">Or continue with email</span>
          </div>
        </div>

        <form onSubmit={handleEmailSignIn} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="drawer-email">Email</Label>
            <Input
              id="drawer-email"
              name="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <Button type="submit" variant="default" className="w-full gap-2" disabled={isLoading}>
            <Mail className="h-4 w-4" />
            {isLoading ? 'Sending...' : 'Continue with Email'}
            {!isLoading && <ArrowRight className="h-4 w-4" />}
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            We'll send you a secure sign-in link. No password needed.
          </p>
        </form>
      </div>
    </div>
  );
};
